import { useMemo } from 'react';
import { useMutation, useQuery } from 'react-apollo';

import {
  AlbumNjrBibSettings,
  PhotoNjrBibMatches,
} from '../graphql/queries/photos';
import { MeQuery } from '../graphql/queries/account';
import { BulkVote } from '../apps/pro/views/FaceTagPhoto/graphql/mutations';
import useAuthenticated from './useAuthenticated';
import { VOTE_DECISION } from '../constants/vote-decisions';
import { FAUX_FACE_TYPE } from '../constants/face';

const NJR_BIB_FAUX_FACE_TYPES = [
  FAUX_FACE_TYPE.MACHINE_LABELED_PUB,
  FAUX_FACE_TYPE.TEXT_MATCH,
];

const useRemoveNjrBibMatch = ({ photoId, albumId, skip = false }) => {
  const authenticated = useAuthenticated();

  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated || skip,
  });

  const { data: { album } = {} } = useQuery(AlbumNjrBibSettings, {
    variables: { albumId },
    skip: !albumId || skip,
    fetchPolicy: 'cache-first',
  });

  const isNjrBibAlbum = useMemo(() => {
    const uniformTag = album?.root_album_folder?.uniform_tags?.records?.[0];

    const isNumberedJersey =
      !!uniformTag?.uniform_enabled && !!uniformTag?.uniform_numbered;
    const isBib = !!uniformTag?.text_matching_enabled;

    return isNumberedJersey || isBib;
  }, [album]);

  const {
    data: { photo } = {},
    loading,
    refetch,
  } = useQuery(PhotoNjrBibMatches, {
    variables: { id: photoId, albumIds: albumId ? [albumId] : [] },
    skip: !photoId || !isNjrBibAlbum || skip,
    fetchPolicy: 'cache-and-network',
  });

  const matches = useMemo(() => {
    const faces = photo?.faces?.records ?? [];

    return faces
      .filter(face => NJR_BIB_FAUX_FACE_TYPES.includes(face?.faux_face_type))
      .flatMap(face =>
        (face?.matched_photos?.records ?? [])
          .filter(matchedPhoto => !!matchedPhoto?.identity)
          .map(matchedPhoto => ({
            id: matchedPhoto.id,
            comparedFaceId: face.id,
            identity: matchedPhoto.identity,
          })),
      );
  }, [photo]);

  const [bulkVote, { loading: removing }] = useMutation(BulkVote);

  const removeMatches = async (matchesToRemove = matches) => {
    const votes = (matchesToRemove ?? [])
      .filter(match => !!match?.identity)
      .map(match => ({
        compared_photo_id: photoId,
        compared_face_id: match.comparedFaceId,
        identity_id: match.identity.id,
        selfie_face_id: match.identity.current_matching_face?.id,
        judge_id: user?.id,
        decision: VOTE_DECISION.NOPE,
      }));

    if (!user?.id || votes.length === 0) {
      return;
    }

    await bulkVote({ variables: { input: { votes } } });

    await refetch();
  };

  return {
    matches,
    hasMatches: isNjrBibAlbum && matches.length > 0,
    isNjrBibAlbum,
    loading,
    removing,
    removeMatches,
    refetch,
  };
};

export default useRemoveNjrBibMatch;
